import * as React from "react";
import { useRouteLoaderData } from "react-router";
import { controlVariantOf, EXPERIMENTS, type ExperimentKey, type VariantOf } from "./registry";
import type { ExperimentsPayload } from "./types";

const EMPTY_PAYLOAD: ExperimentsPayload = { assignments: {}, forced: [] };

// Optional override, for dev pages and component tests that want to render an arm
// directly. In the app nothing provides it — the value comes from the root
// loader, which is the whole point:
//
// the variant MUST come from `useRouteLoaderData("root")` and nowhere else. It is
// decided server-side and is already in the initial HTML, so the first paint is
// the assigned arm. Reading it from `~/lib/use-cookie-state.ts` instead would
// render control on the server (its `getServerSnapshot` always returns the
// default) and flip after hydration — exactly the flicker this design exists to
// prevent.
const ExperimentsContext = React.createContext<ExperimentsPayload | null>(null);

export function ExperimentsProvider({
  value,
  children,
}: {
  value: ExperimentsPayload;
  children: React.ReactNode;
}) {
  return <ExperimentsContext.Provider value={value}>{children}</ExperimentsContext.Provider>;
}

export function useExperiments(): ExperimentsPayload {
  const override = React.useContext(ExperimentsContext);
  const rootData = useRouteLoaderData("root") as { experiments?: ExperimentsPayload } | undefined;
  return override ?? rootData?.experiments ?? EMPTY_PAYLOAD;
}

/**
 * The visitor's arm for one experiment, falling back to control when the
 * experiment did not assign on this route (or at all).
 */
export function useVariant<K extends ExperimentKey>(key: K): VariantOf<K> {
  const { assignments } = useExperiments();
  const assigned = assignments[key];
  const isValid =
    assigned != null && Object.prototype.hasOwnProperty.call(EXPERIMENTS[key].variants, assigned);
  return (isValid ? assigned : controlVariantOf(key)) as VariantOf<K>;
}
